import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, IconButton } from '@mui/material';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import type { GridApiPro, GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import type { GetArAgingReportResponse } from '@vy/api-definition';
import type { ItemOf } from '@vy/utils';
import React, { useCallback, useMemo, useState } from 'react';
import { EmptyTableOverlay } from '../../../../../components/design/EmptyTableOverlay';
import {
  hideColumnSeparatorsSx,
  suppressPinnedColumnSeparatorSx,
} from '../../../../../components/new-design/table/vy-table/pinned-columns-sx';
import { LoadingOverlay } from '../../../../../components/new-design/table/vy-table/VyTable';
import { TOTALS_ROW_ID } from '../../../reports-components/report-table-consts';
import { AR_AGING_REPORT_TABLE_COLUMNS } from './ArAgingReportTableColumns';
import { ArAgingInvoiceDetailPanel } from './ArAgingInvoiceDetailPanel';

type SummaryItem = ItemOf<GetArAgingReportResponse['summary']>;
type BreakdownItem = ItemOf<GetArAgingReportResponse['summaryBreakdown']>;

type ArAgingReportTableProps = {
  items: SummaryItem[];
  breakdownItems: BreakdownItem[];
  loadingItems?: boolean;
  pinnedRows?: { bottom: readonly SummaryItem[] };
  apiRef?: React.MutableRefObject<GridApiPro>;
};

export const ArAgingReportTable: React.FC<ArAgingReportTableProps> = ({
  items,
  breakdownItems,
  loadingItems,
  pinnedRows,
  apiRef,
}) => {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());

  const breakdownByCustomerId = useMemo(() => {
    const map = new Map<string, BreakdownItem[]>();
    for (const item of breakdownItems) {
      const customerId = item.customer.id;
      const existing = map.get(customerId) ?? [];
      existing.push(item);
      map.set(customerId, existing);
    }
    return map;
  }, [breakdownItems]);

  const getDetailPanelContent = useCallback(
    (params: { row: SummaryItem }) => {
      const invoices = breakdownByCustomerId.get(params.row.customer.id) ?? [];
      if (invoices.length === 0) return null;
      return <ArAgingInvoiceDetailPanel invoices={invoices} />;
    },
    [breakdownByCustomerId],
  );

  const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

  const handleDetailPanelExpandedRowIdsChange = useCallback((newIds: Set<GridRowId>) => {
    setExpandedRowIds(newIds);
  }, []);

  const columns = useMemo((): GridColDef<SummaryItem>[] => {
    return [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 40,
        minWidth: 40,
        renderCell: (params) => {
          if (params.row.customer.id === TOTALS_ROW_ID) {
            return null;
          }

          const isExpanded = Boolean(params.value);

          return (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                apiRef?.current.toggleDetailPanel(params.id);
              }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  width: 16,
                  height: 16,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms ease',
                }}
              />
            </IconButton>
          );
        },
      },
      ...AR_AGING_REPORT_TABLE_COLUMNS,
    ];
  }, [apiRef]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      '& .MuiDataGrid-pinnedRows--bottom': {
        marginTop: '-1px',
      },
    }}>
      <DataGridPro
        apiRef={apiRef}
        rows={items}
        columns={columns}
        getRowId={(row) => row.customer.id}
        loading={loadingItems}
        pinnedRows={pinnedRows}
        pinnedColumns={{ left: ['__detail_panel_toggle__', 'customerName'], right: ['totalGrossAmount'] }}
        density="compact"
        pagination
        detailPanelExpandedRowIds={expandedRowIds}
        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        sx={{
          '& .MuiDataGrid-pinnedRows': {
            boxShadow: 'none !important',
            backgroundColor: 'common.white',
          },
          '& .MuiDataGrid-pinnedRows--bottom': {
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'common.white',
          },
          ...suppressPinnedColumnSeparatorSx,
          ...hideColumnSeparatorsSx,
          '& .MuiDataGrid-cell[data-field="customerName"], & .MuiDataGrid-columnHeader[data-field="customerName"]': {
            borderRight: '1px solid #DDDDDD',
          },
          '& .MuiDataGrid-cell[data-field="totalGrossAmount"], & .MuiDataGrid-columnHeader[data-field="totalGrossAmount"]': {
            borderLeft: '1px solid #DDDDDD',
          },
          // MUI X v9 gives every .MuiDataGrid-cell `padding: 0 10px` plus
          // `text-overflow: ellipsis`. The 40px-wide detail-panel-toggle column is
          // left with a 20px content box, so the 26px toggle IconButton overflows
          // and the cell paints a stray ellipsis next to the chevron. Drop the
          // padding (an icon-only column needs none) and clip instead of ellipsize.
          '& .MuiDataGrid-cell[data-field="__detail_panel_toggle__"], & .MuiDataGrid-columnHeader[data-field="__detail_panel_toggle__"]': {
            padding: 0,
            textOverflow: 'clip',
          },
          '& .MuiDataGrid-detailPanel': {
            overflow: 'visible',
          },
        }}
        slots={{
          noRowsOverlay: EmptyTableOverlay,
          loadingOverlay: LoadingOverlay,
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
      />
    </Box>
  );
};
